import { useCallback, useEffect, useRef, useState } from 'react'
import {
  bookPdfReaderSource,
  type BookPdfReaderApiError,
  type BookPdfSlot,
} from '@services/firebase/book-pdf-reader'
import type { ProtectedPageSource, ProtectedReaderSession } from './flipbook-contracts'
import { getFlipbookPerformanceDiagnostics, type FlipbookPageRequestKind } from './flipbook-performance-diagnostics'

const MAX_CACHED_PAGES_DESKTOP = 24
const MAX_CACHED_PAGES_CONSTRAINED = 12
const MAX_PREFETCH_WINDOW_PAGES_DESKTOP = 12
const MAX_PREFETCH_WINDOW_PAGES_CONSTRAINED = 6
const MAX_PREFETCH_CONCURRENCY = 2
const PREFETCH_IDLE_DELAY_MS = 120

interface AdaptivePrefetchProfile {
  readonly cacheLimit: number
  readonly concurrency: number
  readonly isConstrained: boolean
  readonly windowSize: number
}

interface CachedPage {
  readonly lastUsedAt: number
  readonly url: string
}

interface PageResource {
  readonly sessionToken: string
  readonly url: string
}

interface UseProtectedPageResourcesOptions {
  readonly bookId: string | null
  readonly slot: BookPdfSlot
  readonly source?: ProtectedPageSource<BookPdfSlot>
}

interface LoadPagesInput {
  readonly prefetchPages?: readonly number[]
  readonly visiblePages: readonly number[]
}

interface RenewSessionOptions {
  readonly preserveReaderSurface?: boolean
}

interface ProtectedPageResources {
  readonly errorMessage: string | null
  readonly isPageLoading: boolean
  readonly isSessionLoading: boolean
  readonly loadPages: (input: LoadPagesInput) => Promise<void>
  readonly loadPreview: (page: number) => Promise<string | null>
  readonly pageImages: Record<number, string>
  readonly renewSession: (options?: RenewSessionOptions) => Promise<void>
  readonly sessionResetKey: number
  readonly session: ProtectedReaderSession | null
}

function getReadableError(error: unknown) {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }

  return 'This protected PDF could not be loaded.'
}

function isSessionFailure(error: unknown): error is BookPdfReaderApiError {
  return error instanceof Error && 'isSessionFailure' in error && error.isSessionFailure === true
}

function orderAdjacentPages(pages: readonly number[], visiblePages: readonly number[]) {
  const uniquePages = Array.from(new Set(pages))
  return uniquePages
    .sort((left, right) => {
      const leftDistance = Math.min(...visiblePages.map((page) => Math.abs(page - left)))
      const rightDistance = Math.min(...visiblePages.map((page) => Math.abs(page - right)))
      return leftDistance - rightDistance || left - right
    })
}

function getNetworkInformation() {
  if (typeof navigator === 'undefined') {
    return undefined
  }

  return (navigator as Navigator & {
    readonly connection?: {
      readonly effectiveType?: string
      readonly saveData?: boolean
    }
  }).connection
}

function getAdaptivePrefetchProfile(): AdaptivePrefetchProfile {
  const connection = getNetworkInformation()
  const isSmallViewport = typeof window !== 'undefined' && window.innerWidth < 700
  const isLowCoreDevice = typeof navigator !== 'undefined' && typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4
  const isSlowConnection = connection?.saveData === true || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g'
  const isConstrained = isSmallViewport || isLowCoreDevice || isSlowConnection

  return {
    cacheLimit: isConstrained ? MAX_CACHED_PAGES_CONSTRAINED : MAX_CACHED_PAGES_DESKTOP,
    concurrency: MAX_PREFETCH_CONCURRENCY,
    isConstrained,
    windowSize: isConstrained ? MAX_PREFETCH_WINDOW_PAGES_CONSTRAINED : MAX_PREFETCH_WINDOW_PAGES_DESKTOP,
  }
}

function getSequentialPages(startPage: number, endPage: number, step: 1 | -1) {
  const pages: number[] = []
  for (let page = startPage; step > 0 ? page <= endPage : page >= endPage; page += step) {
    pages.push(page)
  }
  return pages
}

function getAdaptivePrefetchWindow({
  adjacentPages,
  direction,
  pageCount,
  profile,
  visiblePages,
}: {
  readonly adjacentPages: readonly number[]
  readonly direction: 'forward' | 'backward'
  readonly pageCount: number
  readonly profile: AdaptivePrefetchProfile
  readonly visiblePages: readonly number[]
}) {
  if (visiblePages.length === 0 || profile.windowSize <= 0) {
    return []
  }

  const minVisiblePage = Math.min(...visiblePages)
  const maxVisiblePage = Math.max(...visiblePages)
  const forwardPages = getSequentialPages(maxVisiblePage + 1, Math.min(pageCount, maxVisiblePage + profile.windowSize), 1)
  const backwardPages = getSequentialPages(minVisiblePage - 1, Math.max(1, minVisiblePage - profile.windowSize), -1)
  const directionPages = direction === 'backward' ? backwardPages : forwardPages
  const oppositePages = direction === 'backward' ? forwardPages : backwardPages
  const orderedAdjacentPages = orderAdjacentPages(adjacentPages, visiblePages)
  const visiblePageSet = new Set(visiblePages)
  const pages: number[] = []

  for (const page of [...orderedAdjacentPages, ...directionPages, ...oppositePages]) {
    if (visiblePageSet.has(page) || pages.includes(page) || page < 1 || page > pageCount) {
      continue
    }

    pages.push(page)
    if (pages.length >= profile.windowSize) {
      break
    }
  }

  return pages
}

function getRetainedPageWindow(visiblePages: readonly number[], prefetchPages: readonly number[]) {
  return new Set([...visiblePages, ...prefetchPages])
}

export function useProtectedPageResources({
  bookId,
  slot,
  source = bookPdfReaderSource,
}: UseProtectedPageResourcesOptions): ProtectedPageResources {
  const cacheRef = useRef(new Map<number, CachedPage>())
  const cacheClockRef = useRef(0)
  const inFlightRef = useRef(new Map<string, Promise<PageResource>>())
  const isMountedRef = useRef(false)
  const sessionRef = useRef<ProtectedReaderSession | null>(null)
  const requestIdRef = useRef(0)
  const backgroundRequestGenerationRef = useRef(0)
  const retainedBackgroundPagesRef = useRef(new Set<number>())
  const previousVisibleStartRef = useRef<number | null>(null)
  const prefetchTimeoutRef = useRef<number | null>(null)
  const previewRequestIdRef = useRef(0)
  const sessionGenerationRef = useRef(0)
  const renewalPromiseRef = useRef<Promise<ProtectedReaderSession | null> | null>(null)
  const [session, setSession] = useState<ProtectedReaderSession | null>(null)
  const [pageImages, setPageImages] = useState<Record<number, string>>({})
  const [isSessionLoading, setIsSessionLoading] = useState(true)
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [sessionResetKey, setSessionResetKey] = useState(0)

  const revokeCachedPage = useCallback((page: number) => {
    const cachedPage = cacheRef.current.get(page)
    if (!cachedPage) {
      return
    }

    cacheRef.current.delete(page)
    getFlipbookPerformanceDiagnostics().markObjectUrlRevoked()
    URL.revokeObjectURL(cachedPage.url)
    getFlipbookPerformanceDiagnostics().markCacheMutation(cacheRef.current.size)
    setPageImages((images) => {
      if (!images[page]) {
        return images
      }

      const nextImages = { ...images }
      delete nextImages[page]
      return nextImages
    })
  }, [])

  const clearCache = useCallback(() => {
    cacheRef.current.forEach(({ url }) => {
      getFlipbookPerformanceDiagnostics().markObjectUrlRevoked()
      URL.revokeObjectURL(url)
    })
    cacheRef.current.clear()
    getFlipbookPerformanceDiagnostics().markCacheMutation(0)
    setPageImages({})
  }, [])

  const cancelScheduledPrefetch = useCallback(() => {
    if (prefetchTimeoutRef.current === null) {
      return
    }

    window.clearTimeout(prefetchTimeoutRef.current)
    prefetchTimeoutRef.current = null
    getFlipbookPerformanceDiagnostics().markPrefetchWindowCancelled()
  }, [])

  const touchCachedPage = useCallback((page: number) => {
    const cachedPage = cacheRef.current.get(page)
    if (!cachedPage) {
      return
    }

    cacheRef.current.set(page, { ...cachedPage, lastUsedAt: ++cacheClockRef.current })
    getFlipbookPerformanceDiagnostics().markCacheMutation(cacheRef.current.size)
  }, [])

  const pruneCache = useCallback(function pruneCache(protectedPages: ReadonlySet<number>) {
    const profile = getAdaptivePrefetchProfile()
    const cacheFloor = Math.max(profile.cacheLimit, protectedPages.size)
    while (cacheRef.current.size > cacheFloor) {
      const evictionCandidate = [...cacheRef.current.entries()]
        .filter(([cachedPage]) => !protectedPages.has(cachedPage))
        .sort(([, left], [, right]) => left.lastUsedAt - right.lastUsedAt)[0]

      if (!evictionCandidate) {
        break
      }

      revokeCachedPage(evictionCandidate[0])
    }
  }, [revokeCachedPage])

  const storePage = useCallback((page: number, url: string, protectedPages: ReadonlySet<number>) => {
    const previousPage = cacheRef.current.get(page)
    if (previousPage?.url === url) {
      touchCachedPage(page)
      pruneCache(protectedPages)
      return
    }

    if (previousPage) {
      getFlipbookPerformanceDiagnostics().markObjectUrlRevoked()
      URL.revokeObjectURL(previousPage.url)
    }

    cacheRef.current.set(page, { lastUsedAt: ++cacheClockRef.current, url })
    getFlipbookPerformanceDiagnostics().markCacheMutation(cacheRef.current.size)
    setPageImages((images) => ({ ...images, [page]: url }))

    pruneCache(protectedPages)
  }, [pruneCache, touchCachedPage])

  const startSession = useCallback(async ({ preserveReaderSurface = false }: RenewSessionOptions = {}) => {
    const generation = sessionGenerationRef.current + 1
    sessionGenerationRef.current = generation
    requestIdRef.current += 1
    previewRequestIdRef.current += 1
    backgroundRequestGenerationRef.current += 1
    retainedBackgroundPagesRef.current = new Set()
    previousVisibleStartRef.current = null
    inFlightRef.current.clear()
    cancelScheduledPrefetch()
    if (!preserveReaderSurface) {
      clearCache()
      sessionRef.current = null
      setSession(null)
      setSessionResetKey((current) => current + 1)
      setIsSessionLoading(true)
    }
    setErrorMessage(null)

    if (!bookId) {
      setErrorMessage('Select a published book before opening its PDF.')
      setIsSessionLoading(false)
      return null
    }

    try {
      getFlipbookPerformanceDiagnostics().markFirstOpenSessionStart()
      const nextSession = await source.openSession({ bookId, slot })
      if (sessionGenerationRef.current !== generation) {
        return null
      }

      getFlipbookPerformanceDiagnostics().markFirstOpenSessionEnd(true, nextSession.pageCount)
      if (preserveReaderSurface) {
        clearCache()
      }
      sessionRef.current = nextSession
      setSession(nextSession)
      return nextSession
    } catch (error) {
      if (sessionGenerationRef.current === generation) {
        getFlipbookPerformanceDiagnostics().markFirstOpenSessionEnd(false)
        setErrorMessage(getReadableError(error))
      }
      return null
    } finally {
      if (sessionGenerationRef.current === generation) {
        setIsSessionLoading(false)
      }
    }
  }, [bookId, cancelScheduledPrefetch, clearCache, slot, source])

  const renewSession = useCallback(async (options: RenewSessionOptions = {}) => {
    if (renewalPromiseRef.current) {
      await renewalPromiseRef.current
      return
    }

    getFlipbookPerformanceDiagnostics().markSessionRenewal()
    const renewalPromise = startSession(options)
    renewalPromiseRef.current = renewalPromise
    try {
      await renewalPromise
    } finally {
      if (renewalPromiseRef.current === renewalPromise) {
        renewalPromiseRef.current = null
      }
    }
  }, [startSession])

  const requestPageUrl = useCallback(async (page: number, sessionSnapshot: ProtectedReaderSession, allowRenewal: boolean, kind: FlipbookPageRequestKind): Promise<PageResource> => {
    const diagnostics = getFlipbookPerformanceDiagnostics()
    const requestKey = `${sessionSnapshot.sessionToken}:${page}`
    const existingRequest = inFlightRef.current.get(requestKey)
    if (existingRequest) {
      diagnostics.markPageRequestDeduplicated(kind)
      return existingRequest
    }

    const measurementId = diagnostics.startPageRequest(page, kind)
    const request = (async () => {
      let activeSession = sessionSnapshot
      let renewalAttempted = !allowRenewal

      try {
        while (true) {
          try {
            const pageBlob = await source.fetchPage({ page, sessionToken: activeSession.sessionToken })
            const url = URL.createObjectURL(pageBlob)
            diagnostics.markObjectUrlCreated(measurementId)
            diagnostics.endPageRequest(measurementId, kind, true)
            return {
              sessionToken: activeSession.sessionToken,
              url,
            }
          } catch (error) {
            if (renewalAttempted || !isSessionFailure(error)) {
              throw error
            }

            renewalAttempted = true
            await renewSession({ preserveReaderSurface: true })
            const renewedSession = sessionRef.current
            if (!renewedSession) {
              throw error
            }
            activeSession = renewedSession
          }
        }
      } catch (error) {
        diagnostics.endPageRequest(measurementId, kind, false)
        throw error
      }
    })()

    inFlightRef.current.set(requestKey, request)
    const clearRequest = () => {
      if (inFlightRef.current.get(requestKey) === request) {
        inFlightRef.current.delete(requestKey)
      }
    }
    request.then(clearRequest, clearRequest).catch(() => undefined)
    return request
  }, [renewSession, source])

  const commitPageUrl = useCallback((page: number, resource: PageResource, protectedPages: ReadonlySet<number>) => {
    if (!isMountedRef.current || sessionRef.current?.sessionToken !== resource.sessionToken) {
      const cachedPage = cacheRef.current.get(page)
      if (cachedPage?.url !== resource.url) {
        getFlipbookPerformanceDiagnostics().markObjectUrlRevoked()
        URL.revokeObjectURL(resource.url)
      }
      return false
    }

    storePage(page, resource.url, protectedPages)
    return true
  }, [storePage])

  const releaseDroppedPageResource = useCallback((page: number, resource: PageResource, protectedPages: ReadonlySet<number>) => {
    if (sessionRef.current?.sessionToken === resource.sessionToken && retainedBackgroundPagesRef.current.has(page)) {
      commitPageUrl(page, resource, protectedPages)
      return false
    }

    if (retainedBackgroundPagesRef.current.has(page)) {
      return false
    }

    const cachedPage = cacheRef.current.get(page)
    if (cachedPage?.url === resource.url) {
      return false
    }

    getFlipbookPerformanceDiagnostics().markObjectUrlRevoked()
    URL.revokeObjectURL(resource.url)
    return true
  }, [commitPageUrl])

  const prefetchPages = useCallback(async (pages: readonly number[], sessionSnapshot: ProtectedReaderSession, protectedPages: ReadonlySet<number>, generation: number, profile: AdaptivePrefetchProfile) => {
    const diagnostics = getFlipbookPerformanceDiagnostics()
    let nextIndex = 0
    while (nextIndex < pages.length) {
      if (backgroundRequestGenerationRef.current !== generation || sessionRef.current?.sessionToken !== sessionSnapshot.sessionToken) {
        diagnostics.markPrefetchWindowCancelled()
        return
      }

      const batch = pages.slice(nextIndex, nextIndex + profile.concurrency)
      nextIndex += profile.concurrency
      await Promise.allSettled(batch.map(async (page) => {
        if (cacheRef.current.has(page)) {
          touchCachedPage(page)
          diagnostics.markPageRequestCacheHit('prefetch')
          diagnostics.markCacheHit()
          return
        }

        const resource = await requestPageUrl(page, sessionSnapshot, true, 'prefetch')
        if (backgroundRequestGenerationRef.current !== generation || sessionRef.current?.sessionToken !== sessionSnapshot.sessionToken) {
          if (releaseDroppedPageResource(page, resource, protectedPages)) {
            diagnostics.markPageRequestCancelled('prefetch')
          }
          return
        }
        commitPageUrl(page, resource, protectedPages)
      }))
    }
    diagnostics.markPrefetchWindowCompleted()
  }, [commitPageUrl, releaseDroppedPageResource, requestPageUrl, touchCachedPage])

  const schedulePrefetchPages = useCallback(function schedulePrefetchPages(pages: readonly number[], sessionSnapshot: ProtectedReaderSession, protectedPages: ReadonlySet<number>, generation: number, profile: AdaptivePrefetchProfile) {
    cancelScheduledPrefetch()
    const diagnostics = getFlipbookPerformanceDiagnostics()
    diagnostics.markPrefetchWindowScheduled(pages.length, pages.filter((page) => !cacheRef.current.has(page)).length, profile.cacheLimit, profile.isConstrained)

    if (pages.length === 0) {
      diagnostics.markPrefetchWindowCompleted()
      return
    }

    prefetchTimeoutRef.current = window.setTimeout(() => {
      prefetchTimeoutRef.current = null
      void prefetchPages(pages, sessionSnapshot, protectedPages, generation, profile)
    }, PREFETCH_IDLE_DELAY_MS)
  }, [cancelScheduledPrefetch, prefetchPages])

  const loadPages = useCallback(async ({ prefetchPages: adjacentPages = [], visiblePages }: LoadPagesInput) => {
    const sessionSnapshot = sessionRef.current
    if (!sessionSnapshot) {
      return
    }

    backgroundRequestGenerationRef.current += 1
    const backgroundGeneration = backgroundRequestGenerationRef.current
    const visibleStartPage = visiblePages.length > 0 ? Math.min(...visiblePages) : previousVisibleStartRef.current
    const direction = previousVisibleStartRef.current !== null && visibleStartPage !== null && visibleStartPage < previousVisibleStartRef.current
      ? 'backward'
      : 'forward'
    previousVisibleStartRef.current = visibleStartPage
    const profile = getAdaptivePrefetchProfile()
    const prefetchWindowPages = getAdaptivePrefetchWindow({
      adjacentPages,
      direction,
      pageCount: sessionSnapshot.pageCount,
      profile,
      visiblePages,
    })
    const retainedPages = getRetainedPageWindow(visiblePages, prefetchWindowPages)
    retainedBackgroundPagesRef.current = retainedPages
    pruneCache(retainedPages)
    const pagesToLoad = visiblePages.filter((page) => {
      const isCached = cacheRef.current.has(page)
      if (isCached) {
        touchCachedPage(page)
        getFlipbookPerformanceDiagnostics().markPageRequestCacheHit('visible')
        getFlipbookPerformanceDiagnostics().markCacheHit()
      }
      return !isCached
    })
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setErrorMessage(null)

    if (pagesToLoad.length === 0) {
      setIsPageLoading(false)
      schedulePrefetchPages(prefetchWindowPages, sessionSnapshot, retainedPages, backgroundGeneration, profile)
      return
    }

    setIsPageLoading(true)
    const results = await Promise.allSettled(pagesToLoad.map(async (page) => ({
      page,
      resource: await requestPageUrl(page, sessionSnapshot, true, 'visible'),
    })))

    if (requestIdRef.current !== requestId || sessionRef.current?.sessionToken !== sessionSnapshot.sessionToken) {
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const cachedPage = cacheRef.current.get(result.value.page)
          if (cachedPage?.url !== result.value.resource.url) {
            getFlipbookPerformanceDiagnostics().markObjectUrlRevoked()
            URL.revokeObjectURL(result.value.resource.url)
          }
        }
      })
      return
    }

    const failedResult = results.find((result) => result.status === 'rejected')
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        const committed = commitPageUrl(result.value.page, result.value.resource, retainedPages)
        if (committed) {
          getFlipbookPerformanceDiagnostics().markPageTurnVisibleCommit(result.value.page)
        }
      }
    })

    if (failedResult?.status === 'rejected') {
      setErrorMessage(getReadableError(failedResult.reason))
    }
    setIsPageLoading(false)
    schedulePrefetchPages(prefetchWindowPages, sessionSnapshot, retainedPages, backgroundGeneration, profile)
  }, [commitPageUrl, pruneCache, requestPageUrl, schedulePrefetchPages, touchCachedPage])

  const loadPreview = useCallback(async (page: number): Promise<string | null> => {
    const diagnostics = getFlipbookPerformanceDiagnostics()
    diagnostics.markPreviewRequested()
    const previewRequestId = previewRequestIdRef.current + 1
    previewRequestIdRef.current = previewRequestId
    const backgroundGeneration = backgroundRequestGenerationRef.current
    const sessionSnapshot = sessionRef.current
    if (!sessionSnapshot) {
      diagnostics.markPreviewCancelled()
      return null
    }
    if (cacheRef.current.has(page)) {
      touchCachedPage(page)
      diagnostics.markPageRequestCacheHit('preview')
      diagnostics.markCacheHit()
      diagnostics.markPreviewCompleted()
      return cacheRef.current.get(page)?.url ?? null
    }

    try {
      const resource = await requestPageUrl(page, sessionSnapshot, true, 'preview')
      if (
        previewRequestIdRef.current !== previewRequestId ||
        backgroundRequestGenerationRef.current !== backgroundGeneration ||
        sessionRef.current?.sessionToken !== sessionSnapshot.sessionToken
      ) {
        if (releaseDroppedPageResource(page, resource, new Set([page]))) {
          diagnostics.markPageRequestCancelled('preview')
        }
        diagnostics.markPreviewCancelled()
        return null
      }

      const retainedPages = new Set([...retainedBackgroundPagesRef.current, page])
      const committed = commitPageUrl(page, resource, retainedPages)
      if (committed) {
        diagnostics.markPreviewCompleted()
      } else {
        diagnostics.markPreviewCancelled()
      }
      return committed ? resource.url : null
    } catch {
      // The visible progress preview is best effort; the reader keeps its current page state.
      diagnostics.markPreviewCancelled()
      return null
    }
  }, [commitPageUrl, releaseDroppedPageResource, requestPageUrl, touchCachedPage])

  useEffect(() => {
    // The hook synchronizes the resource state with the authenticated reader session.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void startSession()
  }, [startSession])

  useEffect(() => {
    isMountedRef.current = true
    const inFlightRequests = inFlightRef.current
    const cachedPages = cacheRef.current
    return () => {
      isMountedRef.current = false
      sessionGenerationRef.current += 1
      if (prefetchTimeoutRef.current !== null) {
        window.clearTimeout(prefetchTimeoutRef.current)
        prefetchTimeoutRef.current = null
      }
      inFlightRequests.clear()
      cachedPages.forEach(({ url }) => {
        getFlipbookPerformanceDiagnostics().markObjectUrlRevoked()
        URL.revokeObjectURL(url)
      })
      cachedPages.clear()
    }
  }, [])

  return {
    errorMessage,
    isPageLoading,
    isSessionLoading,
    loadPages,
    loadPreview,
    pageImages,
    renewSession,
    sessionResetKey,
    session,
  }
}
